import React, { useRef } from 'react'
import OriginalModal from 'react-modal'
import styled from 'styled-components'
import RemoveIcon from '~/components/icons/ui/24px_bold-remove.svg'
import { scrollContext } from './scrollContext'
import useMedia from 'react-use/lib/useMedia'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;

  @media (max-width: 1024px) {
    padding-bottom: 24px;
  }
`

export const CloseButton = styled.button`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  border: none;
  width: 36px;
  height: 36px;
  z-index: 999;
`
export type ModalStyle =
  | 'fullScreen'
  | 'halfScreen'
  | 'homeScreen'
  | 'floatingScreen'
  | 'biggerTopCardScreen'
  | 'sentinel'
  | 'default'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  scrollY?: boolean
  children: React.ReactNode
  modalStyle?: ModalStyle
  styles?: OriginalModal.Styles
  baseWidth?: number | string
  baseHeight?: number | string
  customStyles?: OriginalModal.Styles
  isNotification?: boolean
  hideCloseButton?: boolean
}

OriginalModal.setAppElement('#app')

export default function Modal({
  isOpen,
  onClose,
  scrollY = false,
  children,
  customStyles,
  modalStyle,
  styles,
  baseWidth = 800,
  baseHeight,
  hideCloseButton = false,
  isNotification = false,
}: ModalProps) {
  const wrapperReference = useRef(null)
  const isMobileLarge = useMedia('(max-width: 1024px)')

  const overlayClassMap: { [key: string]: string } = {
    fullScreen: 'prix-fullscreen-modal-overlay',
    halfScreen: 'prix-half-screen-modal-overlay',
    homeScreen: 'prix-home-screen-modal-overlay',
    floatingScreen: 'prix-floating-screen-modal-overlay',
    biggerTopCardScreen: 'prix-screen-modal-overlay-bigger-card',
    sentinel: 'prix-sentinel-modal-overlay',
    default: 'prix-modal-overlay',
  }

  const overlayClass = modalStyle ? overlayClassMap[modalStyle] : 'prix-modal-overlay'

  if (modalStyle === 'fullScreen') {
    return (
      <OriginalModal
        isOpen={isOpen}
        className='prix-modal'
        overlayClassName={overlayClass}
        style={
          (customStyles = {
            content: {
              borderRadius: '0',
              flex: '1',
              height: '100%',
              margin: '0',
              maxWidth: '100%',
              left: '0',
              top: '0',
              background: '#F8F8F8',
            },
          })
        }
        portalClassName={isNotification ? 'prix-notification-modal' : undefined}
      >
        <Wrapper
          ref={wrapperReference}
          style={scrollY ? { overflowY: 'scroll' } : { overflowY: 'hidden' }}
        >
          <scrollContext.Provider value={wrapperReference}>{children}</scrollContext.Provider>
        </Wrapper>

        <CloseButton
          onClick={event => {
            event.preventDefault()
            onClose()
          }}
          style={{ display: hideCloseButton || isMobileLarge ? 'none' : 'flex' }}
        >
          <RemoveIcon width={20} height={20} fill='#fff' />
        </CloseButton>
      </OriginalModal>
    )
  }

  if (modalStyle === 'sentinel') {
    return (
      <OriginalModal
        isOpen={isOpen}
        onRequestClose={onClose}
        className='prix-sentinel-modal'
        style={{
          content: {
            ...(styles?.content || {}),
            overflowY: scrollY ? 'scroll' : 'initial',
            flex: `0 1 ${typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth}`,
            height: baseHeight
              ? typeof baseHeight === 'number'
                ? `${baseHeight}px`
                : baseHeight
              : 'auto',
            maxWidth: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth,
          },
          overlay: styles?.overlay,
        }}
        overlayClassName='prix-sentinel-modal-overlay'
      >
        {children}
        <CloseButton
          onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.preventDefault()
            onClose()
          }}
        >
          <RemoveIcon width={20} height={20} fill='#ccc' />
        </CloseButton>
      </OriginalModal>
    )
  }

  return (
    <OriginalModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className='prix-modal'
      overlayClassName={overlayClass}
      style={customStyles ? customStyles : undefined}
      portalClassName={isNotification ? 'prix-notification-modal' : undefined}
    >
      <Wrapper
        ref={wrapperReference}
        style={scrollY ? { overflowY: 'scroll' } : { overflowY: 'hidden' }}
      >
        <scrollContext.Provider value={wrapperReference}>{children}</scrollContext.Provider>
      </Wrapper>

      <CloseButton
        onClick={event => {
          event.preventDefault()
          onClose()
        }}
        style={{ display: hideCloseButton ? 'none' : 'flex' }}
      >
        <RemoveIcon width={20} height={20} fill='#fff' />
      </CloseButton>
    </OriginalModal>
  )
}
